
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Compass, Star, HelpCircle, History, Info, Mail, Shield } from 'lucide-react';
import Dashboard from './components/Dashboard';
import { UserProfile } from './types';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { supabase } from './services/supabase';

// Helper function to retry lazy loading on failure with single-refresh guard
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return lazy(async () => {
    const hasRefreshed = window.sessionStorage.getItem('retry_chunk_refreshed') === 'true';
    try {
      const component = await componentImport();
      window.sessionStorage.removeItem('retry_chunk_refreshed');
      return component;
    } catch (error) {
      if (!hasRefreshed) {
        window.sessionStorage.setItem('retry_chunk_refreshed', 'true');
        console.warn("New version detected or chunk load failed. Reloading once to fetch fresh assets...");
        window.location.reload();
        return new Promise(() => {});
      }
      console.error("Chunk loading failed after refresh:", error);
      throw error;
    }
  });
};

// Lazy load heavy page components for code splitting & minimal initial bundle
const Auth = lazyWithRetry(() => import('./components/Auth'));
const PersonalityAnalysis = lazyWithRetry(() => import('./components/PersonalityAnalysis'));
const PricingModal = lazyWithRetry(() => import('./components/PricingModal'));
const Chatbot = lazyWithRetry(() => import('./components/Chatbot'));
const EnglishReport = lazyWithRetry(() => import('./components/EnglishReport'));
const DeveloperChart = lazyWithRetry(() => import('./components/DeveloperChart'));
const DisqusComments = lazyWithRetry(() => import('./components/DisqusComments'));
const Privacy = lazyWithRetry(() => import('./components/Privacy'));
const Terms = lazyWithRetry(() => import('./components/Terms'));
const About = lazyWithRetry(() => import('./components/About'));
const Contact = lazyWithRetry(() => import('./components/Contact'));
const HowToUse = lazyWithRetry(() => import('./components/HowToUse'));
const Testimonials = lazyWithRetry(() => import('./components/Testimonials'));
const SajuGuide = lazyWithRetry(() => import('./components/SajuGuide'));
const MyPage = lazyWithRetry(() => import('./components/MyPage'));

// Sidebar 컴포넌트
const Sidebar = ({ hasData, onReset, isLoading }: { hasData: boolean, onReset: () => void, isLoading: boolean }) => {
  const { session, dbUser, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path: string) => location.pathname === path;

  const [apiKey, setApiKey] = useState(() => {
    return localStorage.getItem('gemini_api_key') || '';
  });
  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('gemini_selected_model') || 'gemini-2.5-flash';
  });

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const handleSaveSettings = () => {
    localStorage.setItem('gemini_api_key', apiKey);
    localStorage.setItem('gemini_selected_model', selectedModel);
    alert('Settings saved successfully.');
    window.location.reload();
  };

  const userDisplayName = session?.user?.user_metadata?.full_name || session?.user?.email?.split('@')[0] || 'User';
  const userEmail = session?.user?.email || '';
  const userAvatar = session?.user?.user_metadata?.avatar_url || null;

  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 w-full border-b border-gray-200 dark:border-surface-border bg-white/80 dark:bg-background-dark/80 backdrop-blur-md px-4 h-16 flex items-center justify-between shrink-0">
        <Link className="flex items-center gap-2" to="/">
          <span className="text-xl">🌌</span>
          <h2 className="text-xl font-black tracking-tighter text-slate-900 dark:text-white">Cosmic Saju</h2>
        </Link>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle Mobile Navigation Menu"
          className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-slate-800 dark:text-gray-200 cursor-pointer"
        >
          {isMobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          )}
        </button>
      </header>

      {/* Sidebar Container */}
      <aside className={`fixed inset-y-0 left-0 z-50 lg:z-30 w-72 bg-white dark:bg-slate-900 border-r border-gray-250 dark:border-surface-border flex flex-col justify-between p-6 transition-transform duration-300 lg:translate-x-0 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        
        <div className="space-y-8 overflow-y-auto pr-1">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link className="flex items-center gap-2 group" to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="text-2xl">🌌</span>
              <h2 className="text-2xl font-black tracking-tighter text-slate-900 dark:text-white group-hover:text-primary transition-colors">Cosmic Saju</h2>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col gap-2">
            <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Navigation</div>
            
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 px-4 py-3 mb-2 rounded-xl text-sm font-bold text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-850 hover:text-primary transition-all cursor-pointer border border-gray-200 dark:border-surface-border bg-gray-50 dark:bg-slate-800/40"
            >
              <span className="text-sm">{theme === 'dark' ? '☀️' : '🌙'}</span>
              <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            {session && !session.user?.is_anonymous && (
              <Link 
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                  isActive('/mypage') 
                    ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                    : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
                }`} 
                to="/mypage" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <History className="w-5 h-5" />
                My Readings
              </Link>
            )}
            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/saju-guide') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/saju-guide" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Compass className="w-5 h-5" />
              Saju Guide
            </Link>
            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/testimonials') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/testimonials" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Star className="w-5 h-5" />
              Testimonials
            </Link>
            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/how-to-use') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/how-to-use" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <HelpCircle className="w-5 h-5" />
              How to Use
            </Link>

            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/about') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/about" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Info className="w-5 h-5" />
              About Us
            </Link>
            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/contact') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/contact" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Mail className="w-5 h-5" />
              Contact
            </Link>
            <Link 
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                isActive('/privacy') 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                  : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
              }`} 
              to="/privacy" 
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Shield className="w-5 h-5" />
              Privacy Policy
            </Link>

            {(hasData || isLoading) && (
              <Link 
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                  isActive('/report') 
                    ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                    : 'text-slate-600 dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-primary'
                }`} 
                to="/report" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Your Report
              </Link>
            )}
          </nav>
        </div>

        {/* Profile Card & Session Control */}
        <div className="mt-auto pt-6 border-t border-gray-100 dark:border-surface-border">
          {session ? (
            <div className="bg-slate-50 dark:bg-slate-800/80 border border-gray-200 dark:border-surface-border/50 rounded-2xl p-4 space-y-3">
              {/* User Identity */}
              <div className="flex items-center gap-3">
                {userAvatar ? (
                  <img src={userAvatar} alt="Avatar" width="40" height="40" className="size-10 rounded-full border border-slate-750 object-cover" />
                ) : (
                  <div className="size-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    {userDisplayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="overflow-hidden">
                  <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{userDisplayName}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{userEmail}</div>
                </div>
              </div>

              {/* Credits & Upgrade / Manage Subscription */}
              <div className="flex items-center gap-2">
                <div className="flex-1 py-2 px-3 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-surface-border rounded-xl flex items-center justify-between shadow-sm">
                  <span className="opacity-70">Credits</span>
                  <span className="text-primary font-black">{dbUser?.credits || 0}</span>
                </div>
                
                {dbUser && (dbUser.credits > 0 || dbUser.plan !== 'free') ? (
                  <button
                    onClick={() => {
                      const email = dbUser?.email || userEmail;
                      if (!email) {
                        alert('Unable to retrieve email details. Please log in again.');
                        return;
                      }
                      window.open(`/api/polar/portal?email=${encodeURIComponent(email)}`, '_blank');
                    }}
                    className="py-2 px-3 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200 dark:border-slate-700 shadow-sm"
                  >
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span>Manage</span>
                  </button>
                ) : dbUser && dbUser.credits === 0 ? (
                  <button
                    onClick={() => setIsPricingModalOpen(true)}
                    className="py-2 px-3 text-xs font-bold text-white bg-primary hover:bg-primary-dark rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-transparent shadow-sm"
                  >
                    <svg className="w-4 h-4 text-yellow-400 fill-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                    <span>Recharge</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsPricingModalOpen(true)}
                    className="py-2 px-3 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-transparent shadow-sm"
                  >
                    <svg className="w-4 h-4 text-yellow-400 fill-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                    <span>Upgrade</span>
                  </button>
                )}
              </div>

              {/* Customer Portal Button */}
              <button
                onClick={() => {
                  const email = dbUser?.email || userEmail;
                  if (!email) {
                    alert('Unable to retrieve email details. Please log in again.');
                    return;
                  }
                  window.open(`/api/polar/portal?email=${encodeURIComponent(email)}`, '_blank');
                }}
                className="w-full py-2 px-3 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200 dark:border-slate-700 shadow-sm"
              >
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span>Customer Portal</span>
              </button>

              {/* Sign out Button */}
              <button
                onClick={signOut}
                className="w-full py-2 px-3 text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors flex items-center justify-center gap-2 cursor-pointer rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                </svg>
                <span>Sign out</span>
              </button>
            </div>
          ) : (
            <Link 
              className="w-full py-3 px-4 text-sm font-bold text-center text-white bg-primary hover:bg-primary/95 rounded-xl transition-all block"
              to="/auth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Login
            </Link>
          )}
        </div>
      </aside>

      {/* Backdrop for Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          onClick={() => setIsMobileMenuOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        ></div>
      )}

      {/* PricingModal Portal */}
      <Suspense fallback={null}>
        {isPricingModalOpen && (
          <PricingModal isOpen={isPricingModalOpen} onClose={() => setIsPricingModalOpen(false)} />
        )}
      </Suspense>
    </>
  );
};

const AppContent: React.FC = () => {
  const [showChat, setShowChat] = useState(false);
  const [isChatMaximized, setIsChatMaximized] = useState(false);
  const { session, dbUser, refreshDbUser } = useAuth();
  const [sajuData, setSajuData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFreeReport, setIsFreeReport] = useState<boolean>(() => {
    return localStorage.getItem('cosmic_saju_is_free') === 'true';
  });
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    const saved = localStorage.getItem('cosmic_saju_profile') || localStorage.getItem('bluwings_profile');
    return saved ? JSON.parse(saved) : null;
  });

  // Removed automatic data recovery to prevent starting AI analysis on application load.

  const recoverData = async (profile: UserProfile, redirectTo: string = '/report') => {
    // 크레딧 보유 여부로 유료/무료 분기
    const hasCredits = session && dbUser && (dbUser.credits || 0) >= 40;

    if (hasCredits) {
      const confirmAnalysis = window.confirm('Do you want to proceed with the analysis? 40 credits will be deducted.');
      if (!confirmAnalysis) {
        return;
      }
      setIsFreeReport(false);
      localStorage.setItem('cosmic_saju_is_free', 'false');
    } else {
      // 비로그인 또는 크레딧 부족 상태 - 무료간명(gemma-4-31b-it 사용) 진행
      const confirmAnalysis = window.confirm('Do you want to proceed with the free analysis? (AdSense ads will be shown on the report page)');
      if (!confirmAnalysis) {
        return;
      }
      setIsFreeReport(true);
      localStorage.setItem('cosmic_saju_is_free', 'true');
    }

    setSajuData(null);
    setIsLoading(true);

    try {
      const { getCorrectedProfile } = await import('./services/timeCorrection');
      const { generateSajuAnalysis } = await import('./services/gemini');
      const corrected = getCorrectedProfile(profile);
      const data = await generateSajuAnalysis(corrected, !hasCredits);
      if (data) {
        // 보고서 데이터가 정상 생성된 후 크레딧 차감
        if (hasCredits && session) {
          const currentCredits = dbUser?.credits || 0;
          const { error } = await supabase
            .from('users')
            .update({ credits: Math.max(0, currentCredits - 40) })
            .eq('id', session.user.id);
          
          if (error) {
            console.error('Failed to deduct credits:', error.message);
          } else {
            await refreshDbUser();
          }
        }

        // 로그인 상태이고 익명(게스트)이 아니며 유료 분석인 경우에만 Supabase saju_reports 테이블에 분석 기록 자동 저장
        if (session && !session.user?.is_anonymous && hasCredits) {
          try {
            const { error: saveError } = await supabase
              .from('saju_reports')
              .insert({
                user_id: session.user.id,
                profile: profile,
                report_data: data,
                is_free: false
              });
            if (saveError) {
              console.error('Failed to save report to database:', saveError.message);
            } else {
              console.log('Report saved successfully to database.');
            }
          } catch (saveErr) {
            console.error('Failed to save report to database:', saveErr);
          }
        }

        setSajuData(data);
        navigate(redirectTo);
      } else {
        alert('Failed to generate your cosmic blueprint. Please try again.');
      }
    } catch (e) {
      console.error("Analysis failed", e);
      alert('An error occurred during analysis.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = (profile: UserProfile, redirectTo?: string) => {
    setSajuData(null);
    setUserProfile(profile);
    localStorage.setItem('cosmic_saju_profile', JSON.stringify(profile));
    recoverData(profile, redirectTo);
  };

  const clearProfile = () => {
    setUserProfile(null);
    setSajuData(null);
    localStorage.removeItem('cosmic_saju_profile');
    localStorage.removeItem('bluwings_profile');
    navigate('/');
  };

  const saveProfileForDev = (profile: UserProfile, shouldNavigate: boolean = true) => {
    setUserProfile(profile);
    localStorage.setItem('cosmic_saju_profile', JSON.stringify(profile));
    if (shouldNavigate) {
      navigate('/dev-chart');
    }
  };

  const loadReport = (profile: UserProfile, reportData: any, isFree: boolean) => {
    setUserProfile(profile);
    setSajuData(reportData);
    setIsFreeReport(isFree);
    localStorage.setItem('cosmic_saju_profile', JSON.stringify(profile));
    localStorage.setItem('cosmic_saju_is_free', isFree ? 'true' : 'false');
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-transparent text-slate-900 dark:text-white font-body">
      <Sidebar hasData={!!sajuData} onReset={clearProfile} isLoading={isLoading} />
      <main className="flex-1 flex flex-col lg:pl-72 w-full">
        <Suspense fallback={
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="size-12 rounded-full border-t-2 border-primary animate-spin"></div>
          </div>
        }>
          <Routes>
            <Route path="/auth" element={!session ? <Auth /> : <Navigate to="/" replace />} />
            <Route path="/analysis" element={<PersonalityAnalysis />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-to-use" element={<HowToUse />} />
            <Route path="/testimonials" element={<Testimonials />} />
             <Route path="/saju-guide" element={<SajuGuide />} />
            <Route path="/mypage" element={<MyPage onLoadReport={loadReport} />} />
            <Route path="/" element={
              <Dashboard 
                userProfile={userProfile} 
                onSaveProfile={saveProfile} 
                onClearProfile={clearProfile}
                onAnalysisComplete={setSajuData}
                existingData={sajuData}
                isLoading={isLoading}
                onSaveProfileForDev={saveProfileForDev}
              />
            } />
            <Route path="/report" element={
              <EnglishReport userProfile={userProfile} sajuData={sajuData} onReset={clearProfile} isLoading={isLoading} isFree={isFreeReport} />
            } />
            <Route path="/dev-chart" element={
              <DeveloperChart 
                userProfile={userProfile} 
                onSaveProfile={saveProfileForDev} 
                onClearProfile={clearProfile}
                onAnalysisComplete={setSajuData}
                existingData={sajuData}
                isLoading={isLoading}
              />
            } />
          </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <DisqusComments />
        </Suspense>

        {/* SEO: Footer with internal links for crawlability */}
        <footer className="mt-auto border-t border-gray-200 dark:border-surface-border bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">🌌</span>
                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Cosmic Saju</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">© {new Date().getFullYear()}</span>
              </div>
              <nav aria-label="Footer navigation" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                <Link to="/saju-guide" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">Saju Guide</Link>
                {session && !session.user?.is_anonymous && <Link to="/mypage" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">My Readings</Link>}
                <Link to="/about" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">About</Link>
                <Link to="/how-to-use" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">How to Use</Link>
                <Link to="/testimonials" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">Testimonials</Link>
                <Link to="/contact" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">Contact</Link>
                <Link to="/privacy" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">Terms of Service</Link>
              </nav>
            </div>
            <p className="text-center text-[10px] text-slate-400 dark:text-slate-600 mt-4 leading-relaxed">
              AI-powered Korean Saju (Four Pillars of Destiny) astrology readings. Ancient Eastern wisdom meets modern technology. | Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              <br />
              <span className="text-[9px] text-slate-400/80 dark:text-slate-500">
                Disclaimer: All astrological readings and analyses provided on this site are intended solely for educational, cultural, and self-reflection entertainment purposes. They do not constitute financial, investment, legal, or medical advice.
              </span>
            </p>
          </div>
        </footer>
      </main>
      
      {showChat && (
        <Suspense fallback={null}>
          <div 
            id="chat-window" 
            className={`fixed z-50 bg-white dark:bg-surface-dark border border-slate-200 dark:border-surface-border shadow-2xl rounded-3xl overflow-hidden flex flex-col no-print transition-all duration-300 ease-in-out ${
              isChatMaximized 
                ? 'inset-4 md:inset-10 md:w-auto md:h-auto' 
                : 'bottom-24 right-8 w-[90vw] md:w-[400px] h-[600px]'
            }`}
          >
            <Chatbot 
              onClose={() => {
                setShowChat(false);
                setIsChatMaximized(false);
              }} 
              userProfile={userProfile} 
              sajuData={sajuData} 
              onToggleMaximize={() => setIsChatMaximized(!isChatMaximized)} 
              isMaximized={isChatMaximized} 
            />
          </div>
        </Suspense>
      )}

      {false && (
        <button id="chat-toggle-btn" onClick={() => setShowChat(!showChat)} className="fixed bottom-8 right-8 z-50 size-14 rounded-2xl bg-primary text-white shadow-xl hover:scale-110 transition-all flex items-center justify-center no-print">
          <span className="text-2xl">{showChat ? '❌' : '💬'}</span>
        </button>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
